import {
  ADMIN_ROLE,
  CAREAPP_MANAGER,
  CAREAPP_SUPPORT_1,
  CAREAPP_SUPPORT_2,
  TENANT_ADMINISTRATOR,
  TENANT_ROLES,
} from '@careapp-group/core';
import { createFeatureSelector, createSelector } from '@ngrx/store';
import { KratosAuth, State } from './auth.state';
import { environment } from '../../../environments/environment';
import { getSelectedId, TENANTS } from '../data-list.reducer';

export const getAuthenticationState = createFeatureSelector<State>('auth');

export const createSelectors = (rootReducer: any) => ({
  getLoggedIn: createSelector(
    rootReducer,
    (state: State): boolean => state.auth && (<KratosAuth>state.auth).session?.active === true,
  ),
  getAuth: createSelector(rootReducer, (state: State) => state.auth),
  getError: createSelector(rootReducer, (state: State) => state.error),
  getLoading: createSelector(rootReducer, (state: State) => state.loading),
  getRequestingVerification: createSelector(
    rootReducer,
    (state: State) => state.requestingVerification,
  ),
  getPersisted: createSelector(rootReducer, (state: State) => {
    if (environment.useKratos) {
      return;
    }
    const persisted = { ...state };
    delete persisted.ready;
    delete persisted.loading;
    return persisted;
  }),
  getResetPassword: createSelector(rootReducer, (state: State) => state.resetPassword),
  getLoggedInUser: createSelector(rootReducer, (state: State) => state.user),
  getRoles: createSelector(rootReducer, (state: State) => state.roles),
  hasGlobalRole: createSelector(
    rootReducer,
    (state: State) => state.roles && state.roles.length > 0,
  ),
  isAdmin: createSelector(
    rootReducer,
    (state: State) => state.roles && !!state.roles.find((value) => value === ADMIN_ROLE),
  ),
  isAdminOrManager: createSelector(
    rootReducer,
    (state: State) =>
      state.roles &&
      !!state.roles.find((value) => value === ADMIN_ROLE || value === CAREAPP_MANAGER),
  ),
  highestSupportTier: createSelector(rootReducer, (state: State) => {
    if (state?.roles && !!state.roles.find((value) => value === CAREAPP_MANAGER)) {
      return 3;
    } else if (state?.roles && !!state.roles.find((value) => value === CAREAPP_SUPPORT_2)) {
      return 2;
    } else if (state?.roles && !!state.roles.find((value) => value === CAREAPP_SUPPORT_1)) {
      return 1;
    }
    return 0;
  }),
  isReady: createSelector(rootReducer, (state: State) => (state ? state.ready : false)),
  getLatestSeenReleaseNoteGuid: createSelector(
    rootReducer,
    (state: State) => state.user?.meta?.latestReleaseNoteSeen ?? '',
  ),
  getAnnouncementPaneHintDismissed: createSelector(
    rootReducer,
    (state: State) => state.user?.meta?.announcementPaneHintDismissed ?? false,
  ),
  getTenantInsightsConfig: createSelector(
    rootReducer,
    (state: State) => state.user?.meta?.tenantInsights,
  ),

  getCurrentTenantInsightsConfig: createSelector(
    rootReducer,
    getSelectedId(TENANTS),
    (state: State, tenantId: string | null) => {
      if (
        !tenantId ||
        !state.user?.meta?.tenantInsights ||
        !state.user?.meta?.tenantInsights[tenantId]
      ) {
        return null;
      }
      return state.user?.meta?.tenantInsights?.[tenantId];
    },
  ),

  getCurrentVerification: createSelector(rootReducer, (state: State) => state.currentVerification),
  getCurrentPasswordResetToken: createSelector(
    rootReducer,
    (state: State) => state.currentPasswordResetToken,
  ),
  getPhoneNumber: createSelector(rootReducer, (state: State) =>
    state.user ? state.user.phoneNumber : null,
  ),
  getEmail: createSelector(rootReducer, (state: State) => (state.user ? state.user.email : null)),

  getRecoveryFlow: createSelector(rootReducer, (state: State) => state.accountRecoveryFlow),

  getPasswordChangeFlowId: createSelector(
    rootReducer,
    (state: State) => state.passwordChangeFlowId,
  ),

  getPostLoginRoute: createSelector(rootReducer, (state: State) => state.postLoginRoute),

  getLoginFlow: createSelector(rootReducer, (state: State) => state.loginFlow),
  getRegistrationFlow: createSelector(rootReducer, (state: State) => state.registrationFlow),
  getVerificationFlow: createSelector(rootReducer, (state: State) => state.verificationFlow),

  isNewUser: createSelector(
    rootReducer,
    (state: State) => !!state.verificationFlow || state.registrationFlow === 'complete',
  ),
});

export const auth = createSelectors(getAuthenticationState);
